import React, { useState, useEffect } from "react";
import { useRestaurant } from "../../context/RestaurantContext";
import toast from "react-hot-toast";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const defaultWeek = (open = "13:00", close = "23:00") =>
  DAY_NAMES.map((_, day) => ({ day, isOpen: day !== 0, openingTime: open, closingTime: close }));

const RestaurantSettings = () => {
  const { restaurantSettings, updateSettings, refreshSettings } = useRestaurant();

  const [isOpen, setIsOpen] = useState(true);
  const [weeklyHours, setWeeklyHours] = useState(defaultWeek());
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (restaurantSettings) {
      setIsOpen(restaurantSettings.isOpen);
      setWeeklyHours(
        restaurantSettings.weeklyHours?.length === 7
          ? restaurantSettings.weeklyHours.map(h => ({ day: h.day, isOpen: h.isOpen, openingTime: h.openingTime, closingTime: h.closingTime }))
          : defaultWeek(restaurantSettings.openingTime, restaurantSettings.closingTime)
      );
    }
  }, [restaurantSettings]);

  const updateDay = (day, field, value) => {
    setWeeklyHours(prev => prev.map(h => (h.day === day ? { ...h, [field]: value } : h)));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const result = await updateSettings({
        isOpen,
        weeklyHours,
      });
      
      if (result.success) {
        toast.success("Restaurant settings updated successfully!");
        await refreshSettings();
      } else {
        toast.error(result.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Restaurant Operating Hours
          </h2>
          <p className="text-sm text-gray-500">
            Configure when customers can place orders. All times are in IST (Indian Standard Time).
          </p>
        </div>

        {/* Current Status */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Current Status</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })} IST
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                restaurantSettings.isCurrentlyOpen && restaurantSettings.isOpen
                  ? 'bg-green-500 animate-pulse'
                  : 'bg-red-500'
              }`}></div>
              <span className={`font-semibold ${
                restaurantSettings.isCurrentlyOpen && restaurantSettings.isOpen
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}>
                {restaurantSettings.isCurrentlyOpen && restaurantSettings.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </div>
        </div>

        {/* Manual Override */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Manual Override
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Temporarily close the restaurant regardless of operating hours
              </p>
            </div>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                isOpen ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isOpen ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {!isOpen && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                ⚠️ Restaurant is manually closed. Customers cannot place orders.
              </p>
            </div>
          )}
        </div>

        {/* Weekly Schedule */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700">Weekly Schedule</label>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">
            Untick a day to close it. Each open day uses its own hours.
          </p>
          <div className="space-y-2">
            {weeklyHours.map((h) => (
              <div
                key={h.day}
                className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${h.isOpen ? "bg-white border-gray-200" : "bg-red-50 border-red-200"}`}
              >
                <label className="flex items-center gap-2 w-36 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={h.isOpen}
                    onChange={(e) => updateDay(h.day, "isOpen", e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-800">{DAY_NAMES[h.day]}</span>
                </label>

                {h.isOpen ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={h.openingTime}
                      onChange={(e) => updateDay(h.day, "openingTime", e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-400 text-sm">to</span>
                    <input
                      type="time"
                      value={h.closingTime}
                      onChange={(e) => updateDay(h.day, "closingTime", e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                ) : (
                  <span className="text-sm font-semibold text-red-700">Closed</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="border-t pt-4">
          <button
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantSettings;
